import { useState, useEffect, useCallback } from 'react';
import {
  fetchProcedureSourceMappings, createProcedureSourceMapping, deleteProcedureSourceMapping,
} from '../services/api';
import '../pages/Admin.css';
import '../pages/Payers.css';

// ─────────────────────────────────────────────────────────────────────────────
// Service-key mappings (2026-10-13 amendment, A5) — admins only.
//
// A facility whose coders assign the procedure code (Facility A) sends what each
// visit billed as `charge_lines`: closed-vocabulary keys such as `clinic_visit`,
// never free text. A row here tells the coder which CPT/HCPCS code to consider for
// a key; the claim form shows it beside the line. It is a SUGGESTION — nothing here
// ever writes a claim, and the coder's code is the one billed.
//
// A key the facility has sent that no row covers is listed as unmapped ("flagged for
// mapping"): the source adds keys as it adds services, and they are accepted, never
// refused. Mapping one clears the flag on every claim at once.
//
// Shaped like the source payer mappings on the Payers page.
// ─────────────────────────────────────────────────────────────────────────────

const EMPTY_FORM = { source_system: '', service_key: '', suggested_code: '', suggested_code_system: 'CPT' };

function ServiceKeyMappings({ facilityId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [removeTarget, setRemoveTarget] = useState(null);

  const load = useCallback(async (id) => {
    if (!id) { setData(null); return; }
    setLoading(true);
    setError(null);
    try {
      const res = await fetchProcedureSourceMappings(id);
      setData(res.data);
      // Pre-fill the facility's configured source system: a mapping under any other
      // source system is one this facility's deliveries never match.
      setForm((f) => ({ ...f, source_system: res.data.configured_source_system || f.source_system }));
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load service-key mappings.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(facilityId); }, [facilityId, load]);

  const mapKey = (row) => setForm((f) => ({ ...f, source_system: row.source_system, service_key: row.service_key }));

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!form.source_system.trim() || !form.service_key.trim() || !form.suggested_code.trim()) {
      setError('Source system, the service key and a suggested code are all required.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await createProcedureSourceMapping({
        facility_id: Number(facilityId),
        source_system: form.source_system.trim(),
        service_key: form.service_key.trim(),
        suggested_code: form.suggested_code.trim().toUpperCase(),
        suggested_code_system: form.suggested_code_system,
      });
      setForm((f) => ({ ...EMPTY_FORM, source_system: f.source_system }));
      await load(facilityId);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add mapping.');
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async () => {
    if (!removeTarget) return;
    setSaving(true);
    try {
      await deleteProcedureSourceMapping(removeTarget.id);
      setRemoveTarget(null);
      await load(facilityId);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to remove mapping.');
    } finally {
      setSaving(false);
    }
  };

  if (!facilityId) return null;
  const unmapped = data?.unmapped_keys ?? [];

  return (
    <section className="psm-section">
      <div className="psm-header">
        <div>
          <h2>Source service-key mappings</h2>
          <p>
            The code coders should consider for each billed service a facility’s source system sends. Shown beside
            the line on the claim form as a suggestion; it never fills in or changes a claim.
          </p>
        </div>
      </div>

      {error && <div className="admin-modal-alert error psm-alert"><span>{error}</span></div>}

      <div className="admin-table-card">
        {loading ? (
          <p className="psm-muted">Loading…</p>
        ) : (
          <>
            {unmapped.length > 0 && (
              <div className="psm-note" role="status">
                <strong>{unmapped.length} service key{unmapped.length === 1 ? '' : 's'} need{unmapped.length === 1 ? 's' : ''} mapping</strong>
                <ul className="skm-unmapped">
                  {unmapped.map((k) => (
                    <li key={`${k.source_system}:${k.service_key}`}>
                      <span className="payer-code-badge">{k.service_key}</span>
                      <span className="psm-muted"> · on {k.claim_count} claim{k.claim_count === 1 ? '' : 's'}</span>{' '}
                      <button type="button" className="admin-action-btn" onClick={() => mapKey(k)}>Map</button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Source system</th>
                  <th>Service key</th>
                  <th>Suggested code</th>
                  <th>Added</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {(data?.mappings ?? []).length === 0 && (
                  <tr><td colSpan={5} className="psm-muted">No mappings yet. Coders see each line’s key with no suggestion.</td></tr>
                )}
                {(data?.mappings ?? []).map((m) => (
                  <tr key={m.id}>
                    <td>{m.source_system}</td>
                    <td><span className="payer-code-badge">{m.service_key}</span></td>
                    <td>
                      {m.suggested_code}
                      <span className="psm-muted"> · {m.suggested_code_system}</span>
                      {m.suggested_code_description && <span className="psm-muted"> · {m.suggested_code_description}</span>}
                    </td>
                    <td>{m.created_at ? String(m.created_at).slice(0, 10) : '—'}</td>
                    <td>
                      <button className="admin-action-btn deactivate" onClick={() => setRemoveTarget(m)}>Remove</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <form className="psm-add skm-add" onSubmit={handleAdd}>
              <input
                className="admin-modal-input"
                placeholder="Source system"
                aria-label="Source system"
                value={form.source_system}
                onChange={(e) => setForm((f) => ({ ...f, source_system: e.target.value }))}
              />
              <input
                className="admin-modal-input"
                placeholder="Service key, exactly as sent"
                aria-label="Service key"
                value={form.service_key}
                onChange={(e) => setForm((f) => ({ ...f, service_key: e.target.value }))}
              />
              <input
                className="admin-modal-input"
                placeholder="Suggested code"
                aria-label="Suggested code"
                value={form.suggested_code}
                onChange={(e) => setForm((f) => ({ ...f, suggested_code: e.target.value }))}
              />
              <select
                className="admin-modal-input"
                aria-label="Code system"
                value={form.suggested_code_system}
                onChange={(e) => setForm((f) => ({ ...f, suggested_code_system: e.target.value }))}
              >
                <option value="CPT">CPT</option>
                <option value="HCPCS">HCPCS</option>
              </select>
              <button type="submit" className="admin-create-btn" disabled={saving}>
                {saving ? 'Saving…' : 'Add mapping'}
              </button>
            </form>
          </>
        )}
      </div>

      {removeTarget && (
        <div className="admin-modal-overlay">
          <div className="admin-confirm-dialog">
            <h3>Remove mapping?</h3>
            <p>
              <strong>{removeTarget.service_key}</strong> will stop suggesting{' '}
              <strong>{removeTarget.suggested_code}</strong>. No claim changes; coders will see the key unmapped.
            </p>
            <div className="admin-confirm-actions">
              <button className="admin-modal-btn-cancel" onClick={() => setRemoveTarget(null)} disabled={saving}>Cancel</button>
              <button className="admin-confirm-danger" onClick={handleRemove} disabled={saving}>
                {saving ? 'Removing…' : 'Remove mapping'}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

export default ServiceKeyMappings;
